import React, { useMemo } from 'react';
import {
  Avatar,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import CallOutlinedIcon from '@mui/icons-material/CallOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';

const formatTurnTime = (value) => {
  if (value == null) {
    return '';
  }

  const dateValue = typeof value === 'number' ? new Date(value) : new Date(Number(value));
  if (Number.isNaN(dateValue.getTime())) {
    return '';
  }

  return dateValue.toLocaleTimeString('hy-AM', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

const AgentConversationTimeline = ({
  turns = [],
  copyStatus = 'Պատճենել',
  onCopy,
}) => {
  const safeTurns = useMemo(() => (Array.isArray(turns) ? turns : []), [turns]);

  if (safeTurns.length === 0) {
    return null;
  }

  return (
    <Paper
      elevation={0}
      sx={{
        overflow: 'hidden',
        borderRadius: 4,
        border: '1px solid #e2e8f0',
        background: 'linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)',
      }}
    >
      <Box
        sx={{
          px: { xs: 2, md: 2.5 },
          py: { xs: 2, md: 2.25 },
          borderBottom: '1px solid #e2e8f0',
          background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.03) 0%, rgba(59, 130, 246, 0.06) 100%)',
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent="space-between"
        >
          <Stack spacing={0.75}>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              Երկխոսության տրանսկրիպտ
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Միայն զանգի իրական հերթականությամբ երկխոսությունը։
            </Typography>
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
            <Chip
              variant="outlined"
              label={`${safeTurns.length} խոսքային հատված`}
              sx={{ fontWeight: 700, bgcolor: 'rgba(255,255,255,0.72)' }}
            />
            {typeof onCopy === 'function' && (
              <Button
                variant="outlined"
                startIcon={<ContentCopyOutlinedIcon />}
                onClick={onCopy}
                sx={{ textTransform: 'none', fontWeight: 700 }}
              >
                {copyStatus}
              </Button>
            )}
          </Stack>
        </Stack>
      </Box>

      <Box sx={{ p: { xs: 1.5, md: 2 } }}>
        <Stack spacing={1.25}>
          {safeTurns.map((turn, index) => {
            const isAgent = turn.role === 'agent';
            const timeLabel = formatTurnTime(turn.startedAtMs);
            const metaLabel = timeLabel || '';

            return (
              <Box
                key={`${turn.role}-${index}-${turn.text.slice(0, 16)}`}
                sx={{
                  display: 'flex',
                  justifyContent: isAgent ? 'flex-start' : 'flex-end',
                }}
              >
                <Stack
                  direction={isAgent ? 'row' : 'row-reverse'}
                  spacing={1.25}
                  sx={{
                    width: 'min(100%, 860px)',
                    alignItems: 'flex-start',
                  }}
                >
                  <Avatar
                    sx={{
                      width: 38,
                      height: 38,
                      bgcolor: isAgent ? 'primary.light' : 'success.light',
                      color: isAgent ? 'primary.dark' : 'success.dark',
                    }}
                  >
                    {isAgent ? <SmartToyOutlinedIcon fontSize="small" /> : <CallOutlinedIcon fontSize="small" />}
                  </Avatar>

                  <Box
                    sx={{
                      flex: 1,
                      p: { xs: 1.5, md: 1.75 },
                      borderRadius: 3,
                      bgcolor: isAgent
                        ? 'rgba(37, 99, 235, 0.06)'
                        : 'rgba(22, 163, 74, 0.06)',
                      border: '1px solid',
                      borderColor: isAgent
                        ? 'rgba(37, 99, 235, 0.18)'
                        : 'rgba(22, 163, 74, 0.18)',
                    }}
                  >
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={1}
                      justifyContent="space-between"
                      alignItems={{ xs: 'flex-start', sm: 'center' }}
                      sx={{ mb: 0.75 }}
                    >
                      <Stack direction="row" spacing={0.75} alignItems="center" useFlexGap flexWrap="wrap">
                        <Chip
                          size="small"
                          label={turn.speakerLabel}
                          color={isAgent ? 'primary' : 'success'}
                          variant="outlined"
                        />
                      </Stack>
                      {metaLabel && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                          {metaLabel}
                        </Typography>
                      )}
                    </Stack>

                    <Typography sx={{ lineHeight: 1.8, whiteSpace: 'pre-wrap', fontSize: '1rem' }}>
                      {turn.text}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            );
          })}
        </Stack>
      </Box>
    </Paper>
  );
};

export default AgentConversationTimeline;
